import axios from 'axios';
import { Tenant, DashboardMetrics, PricingTierConfig } from '@/types';

const API_BASE = import.meta.env.VITE_API_BASE || '/api';
const ADMIN_API = `${API_BASE}/portal`;

function authHeaders(token: string) {
  return { headers: { Authorization: `Bearer ${token}` } };
}

export class TenantService {
  static async getAllTenants(token: string): Promise<Tenant[]> {
    const response = await axios.get<Tenant[]>(`${ADMIN_API}/tenants`, authHeaders(token));
    return Array.isArray(response.data) ? response.data : [];
  }

  static async getTenant(token: string, tenantId: string): Promise<Tenant> {
    const response = await axios.get<Tenant>(`${ADMIN_API}/tenants/${tenantId}`, authHeaders(token));
    return response.data;
  }

  static async getDashboardMetrics(token: string): Promise<DashboardMetrics> {
    const response = await axios.get<DashboardMetrics>(`${ADMIN_API}/metrics/dashboard`, authHeaders(token));
    return response.data;
  }

  static async createTenant(token: string, data: {
    name: string;
    domain?: string;
    subscriptionTier?: string;
    orgIndustry?: string;
    orgCountry?: string;
    orgSizeBracket?: string;
    licensedUsers?: number;
  }): Promise<{ id: string }> {
    const response = await axios.post(`${ADMIN_API}/tenants`, data, authHeaders(token));
    return response.data;
  }

  static async updateTenantStatus(token: string, tenantId: string, status: string): Promise<void> {
    await axios.patch(`${ADMIN_API}/tenants/${tenantId}`, { status }, authHeaders(token));
  }

  static async deleteTenant(token: string, tenantId: string): Promise<void> {
    await axios.delete(`${ADMIN_API}/tenants/${tenantId}`, authHeaders(token));
  }

  static async getTenantTrial(token: string, tenantId: string): Promise<any> {
    const response = await axios.get(`${ADMIN_API}/tenants/${tenantId}/trial`, authHeaders(token));
    return response.data;
  }

  static async updateTenantTrial(token: string, tenantId: string, updates: {
    trial_ends_at?: string | null;
    trial_max_users?: number;
    subscription_tier?: string;
    trial_accepted_at?: string | null;
  }): Promise<any> {
    const response = await axios.patch(`${ADMIN_API}/tenants/${tenantId}/trial`, updates, authHeaders(token));
    return response.data;
  }

  static async getTenantPromotions(token: string, tenantId: string, groupId?: string | null): Promise<any[]> {
    const params = groupId ? { groupId } : {};
    const response = await axios.get(`${ADMIN_API}/tenants/${tenantId}/promotions`, {
      ...authHeaders(token),
      params,
    });
    return Array.isArray(response.data) ? response.data : [];
  }

  static async createPromotion(token: string, data: {
    name: string;
    description?: string;
    discount_type?: string;
    discount_value?: number;
    starts_at?: string;
    ends_at?: string;
    applies_to: string;
    target_id?: string;
    band_discounts?: Array<{ min: number; max: number | null; percentOff: number }>;
  }): Promise<{ id: string; name: string }> {
    const response = await axios.post(`${ADMIN_API}/promotions`, data, authHeaders(token));
    return response.data;
  }

  static async deactivatePromotion(token: string, promotionId: string): Promise<void> {
    await axios.delete(`${ADMIN_API}/promotions/${promotionId}`, authHeaders(token));
  }

  static async getPricingConfig(token: string): Promise<PricingTierConfig[]> {
    const response = await axios.get<{ tiers: PricingTierConfig[] }>(`${ADMIN_API}/pricing`, authHeaders(token));
    return Array.isArray(response.data?.tiers) ? response.data.tiers : [];
  }

  static async updatePricingConfig(token: string, tiers: PricingTierConfig[]): Promise<PricingTierConfig[]> {
    const response = await axios.put<{ tiers: PricingTierConfig[] }>(`${ADMIN_API}/pricing`, { tiers }, authHeaders(token));
    return Array.isArray(response.data?.tiers) ? response.data.tiers : [];
  }
}
